// Niezapisane zmiany w edytorze szablonu. Zapis: TemplatesToolbar („Zapisz zmiany”).
// - pola pytań (Tak/Nie, komentarz, warunek) i nazwy sekcji: ostatnia wersja z formularza,
// - kolejność (weight) osobno, żeby edycja pola nie nadpisała kolejności po przeciągnięciu.
// Aktualizacje funkcyjne (setX(prev => …)): wywołania z dawnych renderów nie gubią wcześniejszych zmian.
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { EditableReportQuestion, TemplateEditableSection } from "../../types/Rows";

export interface TemplateContextProps {
  sections: Map<string, TemplateEditableSection>;
  updateSection: (section: TemplateEditableSection) => void;
  questions: Map<string, EditableReportQuestion>;
  updateQuestion: (question: EditableReportQuestion) => void;
  // Kolejność pytań w sekcji (pytania najwyższego poziomu): weight = pozycja od 1
  questionWeights: Map<string, number>;
  setQuestionOrder: (ordered: EditableReportQuestion[]) => void;
  // Kolejność sekcji: weight = pozycja od 1
  sectionWeights: Map<string, number>;
  setSectionOrder: (ordered: TemplateEditableSection[]) => void;
  questionsToSave: () => EditableReportQuestion[];
  sectionsToSave: () => TemplateEditableSection[];
  hasChanges: boolean;
  markSaved: () => void;
}

const noop = () => {};
export const TemplateContextDefaults: TemplateContextProps = {
  sections: new Map(),
  updateSection: noop,
  questions: new Map(),
  updateQuestion: noop,
  questionWeights: new Map(),
  setQuestionOrder: noop,
  sectionWeights: new Map(),
  setSectionOrder: noop,
  questionsToSave: () => [],
  sectionsToSave: () => [],
  hasChanges: false,
  markSaved: noop,
};

export const TemplateContext = createContext<TemplateContextProps>(TemplateContextDefaults);

// Pozycje od 1; zwraca nową mapę tylko z elementami, których kolejność się zmieniła
const withOrder = <T extends { id: string; weight: number }>(prev: Map<string, number>, ordered: T[]) => {
  const next = new Map(prev);
  ordered.forEach((item, i) => {
    if (item.weight === i + 1) next.delete(item.id);
    else next.set(item.id, i + 1);
  });
  return next;
};

// Do zapisu: ostatnia wersja pól + kolejność z mapy kolejności (elementy tylko przestawione też trafiają do zapisu)
export const mergeForSave = <T extends { id: string; weight: number }>(
  edited: Map<string, T>,
  bases: Map<string, T>,
  weights: Map<string, number>
): T[] => {
  const ids = new Set([...edited.keys(), ...weights.keys()]);
  return [...ids]
    .map((id) => {
      const item = edited.get(id) ?? bases.get(id);
      if (!item) return undefined;
      return weights.has(id) ? { ...item, weight: weights.get(id)! } : item;
    })
    .filter((x): x is T => !!x);
};

export const TemplateContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [sections, setSections] = useState(new Map<string, TemplateEditableSection>());
  const [questions, setQuestions] = useState(new Map<string, EditableReportQuestion>());
  const [questionWeights, setQuestionWeights] = useState(new Map<string, number>());
  const [sectionWeights, setSectionWeights] = useState(new Map<string, number>());
  // Ostatnio widziane wersje elementów, które tylko przestawiono (bez edycji pól)
  const [questionBases, setQuestionBases] = useState(new Map<string, EditableReportQuestion>());
  const [sectionBases, setSectionBases] = useState(new Map<string, TemplateEditableSection>());

  const updateQuestion = useCallback((item: EditableReportQuestion) => {
    setQuestions((prev) => new Map(prev).set(item.id, item));
  }, []);

  const updateSection = useCallback((section: TemplateEditableSection) => {
    setSections((prev) => new Map(prev).set(section.id, section));
  }, []);

  const setQuestionOrder = useCallback((ordered: EditableReportQuestion[]) => {
    setQuestionWeights((prev) => withOrder(prev, ordered));
    setQuestionBases((prev) => {
      const next = new Map(prev);
      ordered.forEach((q) => next.set(q.id, q));
      return next;
    });
  }, []);

  const setSectionOrder = useCallback((ordered: TemplateEditableSection[]) => {
    setSectionWeights((prev) => withOrder(prev, ordered));
    setSectionBases((prev) => {
      const next = new Map(prev);
      ordered.forEach((s) => next.set(s.id, s));
      return next;
    });
  }, []);

  const questionsToSave = useCallback(
    () => mergeForSave(questions, questionBases, questionWeights),
    [questions, questionBases, questionWeights]
  );
  const sectionsToSave = useCallback(
    () => mergeForSave(sections, sectionBases, sectionWeights),
    [sections, sectionBases, sectionWeights]
  );

  const markSaved = useCallback(() => {
    setQuestions(new Map());
    setSections(new Map());
    setQuestionWeights(new Map());
    setSectionWeights(new Map());
    setQuestionBases(new Map());
    setSectionBases(new Map());
  }, []);

  const hasChanges = questions.size + sections.size + questionWeights.size + sectionWeights.size > 0;

  const state = useMemo(
    () => ({
      sections, updateSection, questions, updateQuestion,
      questionWeights, setQuestionOrder, sectionWeights, setSectionOrder,
      questionsToSave, sectionsToSave, hasChanges, markSaved,
    }),
    [sections, updateSection, questions, updateQuestion, questionWeights, setQuestionOrder, sectionWeights,
      setSectionOrder, questionsToSave, sectionsToSave, hasChanges, markSaved]
  );

  return <TemplateContext.Provider value={state}>{children}</TemplateContext.Provider>;
};

export const useTemplateContext = () => useContext(TemplateContext);

// Kolejność wyświetlania: niezapisana kolejność, jeśli jest, inaczej z serwera
export const byWeight =
  (weights: Map<string, number>) =>
  <T extends { id: string; weight: number }>(a: T, b: T) =>
    (weights.get(a.id) ?? a.weight) - (weights.get(b.id) ?? b.weight);

// Drzewo pytań sekcji (do 3 poziomów) bez modyfikowania danych z zapytania
export const buildQuestionTree = (items: EditableReportQuestion[]): EditableReportQuestion[] => {
  const nodes = new Map(items.map((q) => [q.id, { ...q, children: [] as EditableReportQuestion[] }]));
  const roots: EditableReportQuestion[] = [];
  nodes.forEach((node) => {
    const parent = node.parentId ? nodes.get(node.parentId) : undefined;
    if (parent) parent.children.push(node);
    else if (!node.parentId) roots.push(node);
  });
  const sortRec = (list: EditableReportQuestion[]) => {
    list.sort((a, b) => a.weight - b.weight);
    list.forEach((q) => sortRec(q.children));
  };
  sortRec(roots);
  return roots;
};
